// The timing of the card motion, shared by the engine (busy windows, the auto-complete's pace, the sounds' landing
// times) and the view (Board's sequences, CardEl's turns, board.css). Two profiles:
// - modern: the 3D look the owner picked (ui-and-options.md "Card turn", "Deal animation");
// - classic: the legacy game's motion, with the 3D effects off (owner 2026-10-23: "Tryb klasyczny"; players
//   reported the new animations made them dizzy). Every move is 300 ms with the legacy sine easing, a drop
//   lands at once, the deal flies off the board, gathers on the stock and spreads to every column at once.
//   .ai/classic-motion.md has the legacy sources.
// ui/effects.ts switches it with the effects (app-entry.tsx), the engine and the view read it at use time.

export interface MotionProfile {
  readonly classic: boolean;
  /** The gather phase of a deal (the engine's first `dealing` commit) for a deck of `cards`. */
  dealGatherMs(cards: number): number;
  /** One dealt card sent after the other (0: every card at once). */
  readonly dealPerCardMs: number;
  /** After the last card is sent, until the deal ends (its flight, + a little). */
  readonly dealTailMs: number;
  /** A dealt card's flight to its column. */
  readonly dealFlightMs: number;
  /** A card's glide to a pile (the sounds land with it). */
  readonly flight: { readonly stock: number; readonly waste: number; readonly foundation: number; readonly autoFoundation: number; readonly tableau: number };
  /** A card turning over. */
  readonly turnMs: number;
  /** A card sent to the stock or the waste, turning there: the undo waits for it (controller heavyAnimations). */
  readonly stockTurnMs: number;
  readonly autoMoveStepMs: number;
  /** The Spider stock deal: one card every ... */
  readonly spiderPerCardMs: number;
  /** ... and input refused until the last one has landed (and turned). */
  readonly spiderDealBusyMs: number;
  /** The recycle's first view step (Board stagedRecycle): the waste back on the stock. */
  readonly recycleStageMs: number;
}

const SPIDER_STEP = 70; // legacy spider.js: 70 ms x i + 10

export const MODERN: MotionProfile = {
  classic: false,
  dealGatherMs: () => 400,
  dealPerCardMs: 45,
  dealTailMs: 500, // the last card's 360 ms flight (board.css), + a little
  dealFlightMs: 360,
  flight: { stock: 400, waste: 400, foundation: 320, autoFoundation: 160, tableau: 180 },
  turnMs: 400,
  stockTurnMs: 400,
  autoMoveStepMs: 150, // owner 2026-10-17: the cards going up twice as fast (was 300)
  spiderPerCardMs: SPIDER_STEP,
  spiderDealBusyMs: SPIDER_STEP * 9 + 400 + 20, // the undo's last 400 ms glide; the deal's 360 fits too
  recycleStageMs: 420, // the 400 ms glide and turn back onto the stock, + a little
};

/** Legacy (card.js animDuration): every move 300 ms. */
export const CLASSIC_MOVE_MS = 300;
/** Legacy card.js setReversed: the card narrows in 110 ms and widens in 110 ms, linear. */
export const CLASSIC_TURN_MS = 220;
/** Legacy deck.js shuffle / deal: the cards fly off the board (300 ms), wait 500 ms, come back onto the stock
 *  one every 8 ms (300 ms each), and then every dealt card flies to its column at once. */
export const CLASSIC_OFF_BOARD_MS = CLASSIC_MOVE_MS + 500;
export const CLASSIC_GATHER_STEP_MS = 8;

export const CLASSIC: MotionProfile = {
  classic: true,
  dealGatherMs: (cards) => CLASSIC_OFF_BOARD_MS + CLASSIC_GATHER_STEP_MS * Math.max(0, cards - 1) + CLASSIC_MOVE_MS + 20,
  dealPerCardMs: 0,
  dealTailMs: CLASSIC_MOVE_MS + 20,
  dealFlightMs: CLASSIC_MOVE_MS,
  flight: { stock: CLASSIC_MOVE_MS, waste: CLASSIC_MOVE_MS, foundation: CLASSIC_MOVE_MS, autoFoundation: CLASSIC_MOVE_MS, tableau: CLASSIC_MOVE_MS },
  turnMs: CLASSIC_TURN_MS,
  stockTurnMs: CLASSIC_MOVE_MS + CLASSIC_TURN_MS, // turn three: the cards turn after they land
  autoMoveStepMs: 300, // legacy game.js autoMoveObvious: periodical(300)
  spiderPerCardMs: SPIDER_STEP,
  spiderDealBusyMs: SPIDER_STEP * 9 + 10 + CLASSIC_MOVE_MS + CLASSIC_TURN_MS + 20, // each card turns after it lands
  recycleStageMs: CLASSIC_MOVE_MS + 100, // legacy deck.js: the first card turns 100 ms after the last one lands
};

let current: MotionProfile = MODERN;
export const motion = (): MotionProfile => current;
export function setClassicMotion(on: boolean): void {
  current = on ? CLASSIC : MODERN;
}

/** Mahjong deals 144 tiles: each leaves 0.4 of a card's wait after the one before (owner's look-first round
 *  2026-10-24: 45 ms a tile made a 7 s deal), about 3 s in all. */
export const MAHJONG_DEAL_SCALE = 0.4;
export const dealPerCardMsFor = (tiles: boolean): number => (tiles ? Math.round(current.dealPerCardMs * MAHJONG_DEAL_SCALE) : current.dealPerCardMs);
